import { addItemButtonSelectors, getSelectorAttribute } from '@vy/app-selectors';
import { filterCommissionProducts, isFixedProduct, isUsageProduct } from '@vy/services';
import type { PlanInputFormFixedProduct } from '@vy/types';
import type { PlanInputFormUsageProduct } from '@vy/types';
import { isNotNullOrUndefined, raise, withDefault } from '@vy/utils';
import type { FieldArrayPath, FieldArrayPathValue } from 'react-hook-form';
import { useFormContext } from 'react-hook-form';
import { Menu, getMenuItemsWithHeaders } from '../../../../components/design/menu';
import { withPrefetch } from '../../../../components/hocs';
import { PlusIcon } from '../../../../components/icons/PlusIcon';
import { prefetchGetPricingTemplates } from '../../../../hooks/requests/use-pricing-template';
import {
  getDefaultCommissionProduct,
  getDefaultCreditPromotion,
  getDefaultPerUnitUsageProduct,
  getDefaultTiersUsageProduct,
  getDefaultVolumeUsageProduct,
} from '../create-plan-page-consts/add-item-button-consts';
import { useGetDefaultProductConfigurations } from '../create-plan-page-hooks/useGetDefaultProductConfigurations';
import {
  PRO_RATA_SETTINGS,
  mapProRataSettingToProductFields,
} from './CreatePlanForm/create-plan-form-types/create-plan-form-sub-types';
import type { NewPlanForm } from './CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from './CreatePlanForm/create-plan-form-types/plan-input-form';

type AddItemButtonProps = {
  onClick?: () => void;
};

const AddItemButton: React.FC<AddItemButtonProps> = ({ onClick }) => {
  const { getValues, setValue } = useFormContext<PlanInputForm, NewPlanForm>();
  const {
    getFixedOneTimeProductConfiguration,
    getCommercialTermsProductConfiguration,
    getSubscriptionProductConfiguration,
  } = useGetDefaultProductConfigurations();

  const fixedProducts = withDefault(getValues('fixedProducts'), []);
  const usageProducts = withDefault(getValues('usageProducts'), []);
  const products = [...fixedProducts, ...usageProducts];
  const [commissionProduct] = filterCommissionProducts(products as PlanInputFormFixedProduct[]);

  const handleProductClick = (
    defaultProductCallback: () => PlanInputFormFixedProduct | PlanInputFormUsageProduct,
    shouldAddProRataSettings = false,
  ) => {
    return () => {
      const defaultProduct = defaultProductCallback();
      const proRataSetting = getValues('proRataSetting') ?? PRO_RATA_SETTINGS.PRO_RATA_AND_CALENDAR_ALIGNED;
      const { proRata, isCalendarAligned } = mapProRataSettingToProductFields(proRataSetting);
      const productWithProRataSettings = shouldAddProRataSettings ? { ...defaultProduct, proRata, isCalendarAligned } : defaultProduct;
      const productsPath = getCorrectFieldArrayPath(productWithProRataSettings);
      const currentProducts = withDefault(getValues(productsPath), []);
      const updatedProducts = [...currentProducts, productWithProRataSettings];
      setValue(productsPath, updatedProducts as FieldArrayPathValue<PlanInputForm, typeof productsPath>);
    };
  };

  const handlePromotionClick = (defaultPromotionCallback: () => PlanInputFormFixedProduct) => {
    return () => {
      const currentPromotions = getValues('promotionProducts') ?? [];
      const defaultPromotion = defaultPromotionCallback();
      const updatedPromotions = [...currentPromotions, defaultPromotion];
      setValue('promotionProducts', updatedPromotions);
    };
  };

  return (
    <Menu
      align="right"
      menuItemsWithHeaders={getMenuItemsWithHeaders(
        {
          onFixedPlanClick: handleProductClick(getFixedOneTimeProductConfiguration),
          onUsagePlanClick: handleProductClick(getDefaultTiersUsageProduct, true),
          onPerUnitPlanClick: handleProductClick(getDefaultPerUnitUsageProduct, true),
          onPromotionsPlanClick: handlePromotionClick(getDefaultCreditPromotion),
          onSubscriptionClick: handleProductClick(getSubscriptionProductConfiguration, true),
          onCommissionClick: handleProductClick(getDefaultCommissionProduct),
          onVolumeClick: handleProductClick(getDefaultVolumeUsageProduct, true),
          onCommercialTermsClick: handleProductClick(getCommercialTermsProductConfiguration),
        },
        {
          commissionItem: {
            disabled: isNotNullOrUndefined(commissionProduct),
          },
        },
      )}
      className="w-auto whitespace-nowrap"
    >
      <button
        className="group mb-[180px] flex w-fit cursor-pointer items-center space-x-1.5"
        onClick={onClick}
        {...getSelectorAttribute(addItemButtonSelectors.button)}
      >
        <PlusIcon className="text-icon  group-hover:text-style" />
        <label className="text-icon group-hover:text-style cursor-pointer">Add item</label>
      </button>
    </Menu>
  );
};

function getCorrectFieldArrayPath(
  product: PlanInputFormFixedProduct | PlanInputFormUsageProduct,
): FieldArrayPath<PlanInputForm> {
  if (isFixedProduct(product)) {
    return 'fixedProducts';
  }

  if (isUsageProduct(product)) {
    return 'usageProducts';
  }

  return raise('Invalid product type');
}

const AddItemButtonWithPrefetch = withPrefetch(AddItemButton, prefetchGetPricingTemplates);

export { AddItemButtonWithPrefetch as AddItemButton };
